/**
 * SideNav.tsx — sidemeny (venstre)
 *
 * Ren React-komponent som erstatter cds-side-nav.
 * Responsiv sidemeny med lenker og utvidbare menyer.
 * Fra 1056 px er den alltid synlig: lukket er den en smal rail med bare ikoner, åpen viser den tekst og dropdowns.
 * Under 1056 px er den et overlegg som er skjult når den er lukket.
 *
 * Props:
 * - expanded: om side-nav er utvidet
 * - onExpand: åpner menyen (en dropdown trykkes i rail-modus)
 * - ariaLabel: tilgjengelighet-label
 * - children: SideNavLink / SideNavMenu / SideNavDivider
 */

import { ReactNode, useMemo, useSyncExternalStore } from 'react';
import { SideNavContext } from './SideNavContext';
import styles from './side-nav.module.scss';

interface SideNavProps {
  expanded: boolean;
  onExpand?: () => void;
  ariaLabel?: string;
  children: ReactNode;
}

const RAIL_QUERY = '(min-width: 1056px)';

function subscribeRail(onChange: () => void) {
  const mq = window.matchMedia(RAIL_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

export default function SideNav({ expanded, onExpand, ariaLabel = 'Sidemeny', children }: SideNavProps) {
  // Bred skjerm: menyen (rail eller åpen) er alltid synlig. Smal skjerm: lukket overlegg er skjult og kan ikke fokuseres.
  const wide = useSyncExternalStore(
    subscribeRail,
    () => window.matchMedia(RAIL_QUERY).matches,
    () => false
  );
  const hidden = !expanded && !wide;
  const state = useMemo(() => ({ expanded, expand: onExpand }), [expanded, onExpand]);

  return (
    <nav
      className={expanded ? styles.sideNavExpanded : styles.sideNav}
      aria-label={ariaLabel}
      aria-hidden={hidden || undefined}
      inert={hidden}
    >
      <SideNavContext.Provider value={state}>
        <ul className={styles.items}>{children}</ul>
      </SideNavContext.Provider>
    </nav>
  );
}
